import { getNodeText } from '@/common/utils/formTitle'

/** Минимальная ширина platform-диалога (раньше 800 — узко на современных экранах). */
export const PLATFORM_DIALOG_MIN_WIDTH = 1200
export const PLATFORM_DIALOG_MAX_WIDTH_RATIO = 0.95
/** Потолок на ultrawide/QHD. */
export const PLATFORM_DIALOG_CONTENT_MAX_PX = 2400
/** Доля viewport для дефолтной ширины, когда meta/estimate не задали размер. */
export const PLATFORM_DIALOG_DEFAULT_VIEWPORT_RATIO = 0.92
export const PLATFORM_DIALOG_MIN_HEIGHT = 400
export const PLATFORM_DIALOG_MAX_HEIGHT_RATIO = 0.92
/** Отступ от краёв браузера для ручного ресайза и «Раскрыть». */
export const PLATFORM_DIALOG_VIEWPORT_INSET_PX = 16
/** Browser-first default height for table-heavy dialogs when XML height is empty. */
export const PLATFORM_DIALOG_TABLE_FORM_HEIGHT_RATIO = 0.88
export const PLATFORM_DIALOG_TABLE_FORM_HEIGHT_CAP_PX = 1100
export const PLATFORM_DIALOG_TABLE_FORM_HEIGHT_MIN_PX = 640

/** Dialog padding + small slack (px). */
export const PLATFORM_DIALOG_CHROME_PX = 80

const TABLE_TYPES = new Set(['Table', 'TreeTable', 'ListboxTable'])
const COLUMN_WIDTH_BY_TYPE: Record<string, number> = {
  Text: 160,
  Date: 110,
  DateTime: 140,
  Time: 90,
  Boolean: 70,
  Numeric: 90,
  Identificator: 80,
  ComboBox: 140,
  Reference: 180,
}
const DEFAULT_COLUMN_WIDTH = 140
const TREETABLE_EXPANDER_PX = 48
const TABLE_CHECKBOX_PX = 48
const SIDEBAR_CELL_MIN_PX = 250

const isXmlTrue = (value: string, defaultValue = false): boolean => {
  const normalized = value.trim().toLowerCase()
  if (!normalized) return defaultValue
  return normalized === '1' || normalized === 'true'
}

const getElementType = (element: unknown): string => getNodeText((element as any)?.type).trim()

const isColumnVisible = (column: unknown): boolean => {
  const col = column as Record<string, unknown>
  const defVisible = getNodeText(col?.defVisible)
  if (defVisible === '0' || defVisible === 'false') {
    return false
  }
  const type = getNodeText(col?.type)
  if (type === 'Identificator') {
    return defVisible === '1' || defVisible === 'true'
  }
  return true
}

const estimateColumnWidth = (column: unknown): number => {
  const col = column as Record<string, unknown>
  const explicit = Number.parseInt(getNodeText(col?.width), 10)
  if (Number.isFinite(explicit) && explicit > 0) {
    return explicit
  }
  const type = getNodeText(col?.type)
  return COLUMN_WIDTH_BY_TYPE[type] ?? DEFAULT_COLUMN_WIDTH
}

const isMultiSelectTable = (element: unknown): boolean => {
  const el = element as Record<string, unknown>
  return isXmlTrue(getNodeText(el?.isMultiSelectEnabled), false)
}

const estimateTableElementWidth = (element: unknown): number => {
  const el = element as Record<string, unknown>
  const type = getElementType(el)
  let width = 0
  if (type === 'TreeTable') {
    width += TREETABLE_EXPANDER_PX
  }
  if (isMultiSelectTable(el)) {
    width += TABLE_CHECKBOX_PX
  }

  const columnsNode = (el?.columns as Record<string, unknown> | undefined)?.column
  if (!columnsNode) {
    return width + DEFAULT_COLUMN_WIDTH * 2
  }

  const columns = Array.isArray(columnsNode) ? columnsNode : [columnsNode]
  let visibleCount = 0
  for (const column of columns) {
    if (!isColumnVisible(column)) continue
    width += estimateColumnWidth(column)
    visibleCount += 1
  }

  if (visibleCount === 0) {
    width += DEFAULT_COLUMN_WIDTH * 2
  }

  return width
}

const estimateElementWidth = (element: unknown): number => {
  const type = getElementType(element)
  if (TABLE_TYPES.has(type)) {
    return estimateTableElementWidth(element)
  }
  if (type === 'MultiSelect') {
    return 320
  }
  if (type === 'TextArea' || type === 'RichText') {
    return 400
  }
  return 0
}

const parseCellWidthPercent = (cell: unknown): number | null => {
  const attrs = (cell as Record<string, unknown>)?._attributes as Record<string, unknown> | undefined
  const raw = getNodeText(attrs?.width).trim()
  if (!raw.endsWith('%')) {
    return null
  }
  const percent = Number.parseFloat(raw.replace('%', ''))
  return Number.isFinite(percent) && percent > 0 ? percent : null
}

const estimateCellWidth = (cell: unknown): number => {
  if (!cell || typeof cell !== 'object') {
    return 0
  }
  const item = cell as Record<string, unknown>
  if (item._isRowspanPlaceholder) {
    return 0
  }

  let content = 0
  if (item.element) {
    content = estimateElementWidth(item.element)
  } else if (Array.isArray(item.rows)) {
    content = estimatePanelWidth(item)
  }

  const percent = parseCellWidthPercent(item)
  if (percent != null && content > 0 && percent <= 35) {
    return Math.max(content, SIDEBAR_CELL_MIN_PX)
  }

  return content
}

const estimatePanelRow = (cells: unknown[]): number => {
  if (!Array.isArray(cells)) {
    return 0
  }

  let sum = 0
  for (const cell of cells) {
    sum += estimateCellWidth(cell)
  }
  return sum
}

const estimatePanelWidth = (panel: unknown): number => {
  const rows = (panel as Record<string, unknown>)?.rows
  if (!Array.isArray(rows)) {
    return 0
  }

  let maxRow = 0
  for (const row of rows) {
    if (row && typeof row === 'object' && !Array.isArray(row) && (row as Record<string, unknown>).element) {
      maxRow = Math.max(maxRow, estimateElementWidth((row as Record<string, unknown>).element))
      continue
    }
    if (Array.isArray(row)) {
      maxRow = Math.max(maxRow, estimatePanelRow(row))
    }
  }
  return maxRow
}

const collectTabs = (form: Record<string, unknown>): unknown[] => {
  const tabs: unknown[] = []

  for (const section of [form.header, form.footer]) {
    if (Array.isArray(section)) {
      tabs.push(...section)
    }
  }

  const main = form.main
  if (Array.isArray(main)) {
    for (const region of main) {
      if (Array.isArray(region)) {
        tabs.push(...region)
      } else if (region && typeof region === 'object' && Array.isArray((region as Record<string, unknown>).panelRows)) {
        tabs.push(region)
      }
    }
  }

  return tabs
}

/**
 * Estimates minimum dialog content width from parsed form metadata (no DOM).
 * Returns 0 when form has no measurable wide content.
 */
export const estimateFormContentWidth = (
  form: Record<string, unknown> | null | undefined,
): number => {
  if (!form || typeof form !== 'object') {
    return 0
  }

  let maxWidth = 0
  for (const tab of collectTabs(form)) {
    const panelRows = (tab as Record<string, unknown>)?.panelRows
    if (!Array.isArray(panelRows)) {
      continue
    }

    for (const panelRow of panelRows) {
      if (!Array.isArray(panelRow)) {
        continue
      }

      const rowWidth = estimatePanelRow(panelRow)
      if (rowWidth > 0) {
        maxWidth = Math.max(maxWidth, rowWidth)
        continue
      }

      for (const panel of panelRow) {
        if (!panel || typeof panel !== 'object') {
          continue
        }
        const panelObj = panel as Record<string, unknown>
        if (panelObj._isRowspanPlaceholder) {
          continue
        }
        maxWidth = Math.max(maxWidth, estimatePanelWidth(panelObj))
      }
    }
  }

  return maxWidth > 0 ? maxWidth + PLATFORM_DIALOG_CHROME_PX : 0
}

export const panelHasTableElement = (panel: unknown): boolean => {
  const rows = (panel as Record<string, unknown>)?.rows
  if (!Array.isArray(rows)) {
    return false
  }

  for (const row of rows) {
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      const element = (row as Record<string, unknown>).element
      if (element && TABLE_TYPES.has(getElementType(element))) {
        return true
      }
      continue
    }
    if (!Array.isArray(row)) {
      continue
    }
    for (const cell of row) {
      if (!cell || typeof cell !== 'object') {
        continue
      }
      const item = cell as Record<string, unknown>
      if (item.element && TABLE_TYPES.has(getElementType(item.element))) {
        return true
      }
      if (Array.isArray(item.rows) && panelHasTableElement(item)) {
        return true
      }
    }
  }
  return false
}

/** True when parsed form metadata contains at least one Table / TreeTable / ListboxTable. */
export const formHasTableElements = (
  form: Record<string, unknown> | null | undefined,
): boolean => {
  if (!form || typeof form !== 'object') {
    return false
  }

  for (const tab of collectTabs(form)) {
    const panelRows = (tab as Record<string, unknown>)?.panelRows
    if (!Array.isArray(panelRows)) {
      continue
    }

    for (const panelRow of panelRows) {
      if (!Array.isArray(panelRow)) {
        continue
      }
      for (const panel of panelRow) {
        if (!panel || typeof panel !== 'object') {
          continue
        }
        const panelObj = panel as Record<string, unknown>
        if (panelObj._isRowspanPlaceholder) {
          continue
        }
        if (panelHasTableElement(panelObj)) {
          return true
        }
      }
    }
  }

  // Fallback: panels registry (layout stubs may not expand rows into tab.panelRows yet).
  const panels = form.panels
  if (panels && typeof panels === 'object' && !Array.isArray(panels)) {
    for (const panel of Object.values(panels as Record<string, unknown>)) {
      if (panelHasTableElement(panel)) {
        return true
      }
    }
  }

  return false
}

/**
 * Suggested dialog height for table-heavy forms (browser-first, not Swing copy).
 * Returns null when form has no tables — caller keeps content-driven height.
 */
export const estimateFormDialogHeight = (
  form: Record<string, unknown> | null | undefined,
  viewport: { height: number },
): number | null => {
  if (!formHasTableElements(form)) {
    return null
  }

  const target = Math.min(
    PLATFORM_DIALOG_TABLE_FORM_HEIGHT_CAP_PX,
    Math.round(viewport.height * PLATFORM_DIALOG_TABLE_FORM_HEIGHT_RATIO),
  )
  return clampDialogHeight(target, viewport)
}

export const clampDialogWidth = (
  widthPx: number,
  viewport: { width: number },
): number => {
  const max = Math.min(
    Math.round(viewport.width * PLATFORM_DIALOG_MAX_WIDTH_RATIO),
    PLATFORM_DIALOG_CONTENT_MAX_PX,
  )
  return Math.max(PLATFORM_DIALOG_MIN_WIDTH, Math.min(widthPx, max))
}

export const clampDialogWidthMax = (
  widthPx: number,
  viewport: { width: number },
): number => {
  const max = Math.min(
    Math.round(viewport.width * PLATFORM_DIALOG_MAX_WIDTH_RATIO),
    PLATFORM_DIALOG_CONTENT_MAX_PX,
  )
  return Math.min(widthPx, max)
}

export const clampDialogHeight = (
  heightPx: number,
  viewport: { height: number },
): number => {
  const max = Math.round(viewport.height * PLATFORM_DIALOG_MAX_HEIGHT_RATIO)
  return Math.max(PLATFORM_DIALOG_MIN_HEIGHT, Math.min(heightPx, max))
}

export const clampDialogHeightMax = (
  heightPx: number,
  viewport: { height: number },
): number => {
  const max = Math.round(viewport.height * PLATFORM_DIALOG_MAX_HEIGHT_RATIO)
  return Math.min(heightPx, max)
}

export const maxResizableDialogWidth = (viewport: { width: number }): number =>
  Math.max(PLATFORM_DIALOG_MIN_WIDTH, viewport.width - 2 * PLATFORM_DIALOG_VIEWPORT_INSET_PX)

export const maxResizableDialogHeight = (viewport: { height: number }): number =>
  Math.max(PLATFORM_DIALOG_MIN_HEIGHT, viewport.height - 2 * PLATFORM_DIALOG_VIEWPORT_INSET_PX)

export const clampDialogResizeWidth = (
  widthPx: number,
  viewport: { width: number },
): number => Math.max(PLATFORM_DIALOG_MIN_WIDTH, Math.min(widthPx, maxResizableDialogWidth(viewport)))

export const clampDialogResizeHeight = (
  heightPx: number,
  viewport: { height: number },
): number => Math.max(PLATFORM_DIALOG_MIN_HEIGHT, Math.min(heightPx, maxResizableDialogHeight(viewport)))

const GROW_ONLY_DIALOG_SLACK_PX = 12

/** Grow dialog height to fit newly shown content; never shrink; skip after user resize. */
export function nextGrowOnlyDialogHeight(args: {
  currentHeightPx: number | null | undefined
  naturalHeightPx: number
  viewport: { height: number }
  userResized: boolean
  slackPx?: number
}): number | null {
  if (args.userResized) return null
  const slack = args.slackPx ?? GROW_ONLY_DIALOG_SLACK_PX
  const capped = Math.min(
    clampDialogHeight(args.naturalHeightPx, args.viewport),
    maxResizableDialogHeight(args.viewport),
  )
  if (args.currentHeightPx == null) return capped
  if (capped > args.currentHeightPx + slack) return capped
  return null
}
